import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, KeyRound, Check, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email, 2: code, 3: success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayCode, setDisplayCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/password-reset/request`, { email });
      if (res.data.code) {
        setDisplayCode(res.data.code);
      }
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al solicitar codigo');
    } finally {
      setLoading(false);
    }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Las contrasenas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      setError('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/password-reset/confirm`, {
        email, code: code.toUpperCase(), new_password: newPassword
      });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Codigo invalido o expirado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <button onClick={() => navigate('/login')} className="flex items-center gap-2 text-white/40 hover:text-white text-sm mb-8 transition-colors">
          <ArrowLeft size={16} /> Volver a iniciar sesion
        </button>

        <div className="text-center mb-10">
          <img src="/m3-logo.png" alt="M3" className="h-16 mx-auto mb-4" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Recuperar Contrasena</p>
        </div>

        {step === 1 && (
          <form onSubmit={requestCode} className="space-y-6">
            <p className="text-sm text-white/50 text-center mb-6">
              Ingresa tu email y te generaremos un codigo de recuperacion que aparecera en tus notificaciones de la app.
            </p>
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-400/10 border border-red-400/30 text-red-400 text-sm">
                <AlertCircle size={16} /> {error}
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 block">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required data-testid="reset-email-input"
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:border-gold focus:outline-none"
                placeholder="tu@email.com" />
            </div>
            <button type="submit" disabled={loading} data-testid="request-code-btn"
              className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50">
              {loading ? 'Enviando...' : 'Solicitar Codigo'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={confirmReset} className="space-y-6">
            {displayCode && (
              <div className="p-4 bg-gold/10 border border-gold/30 text-center">
                <p className="text-xs text-white/60 mb-2">Tu codigo de recuperacion es:</p>
                <p className="text-2xl font-mono font-bold text-gold tracking-widest">{displayCode}</p>
                <p className="text-[10px] text-white/40 mt-2">Este codigo tambien aparece en tus notificaciones</p>
              </div>
            )}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-400/10 border border-red-400/30 text-red-400 text-sm">
                <AlertCircle size={16} /> {error}
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 block">Codigo de Recuperacion</label>
              <input type="text" value={code} onChange={e => setCode(e.target.value.toUpperCase())} required maxLength={6}
                data-testid="reset-code-input"
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm font-mono tracking-widest text-center focus:border-gold focus:outline-none"
                placeholder="XXXXXX" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 block">Nueva Contrasena</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required
                data-testid="new-password-input"
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:border-gold focus:outline-none"
                placeholder="Minimo 6 caracteres" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2 block">Confirmar Contrasena</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required
                data-testid="confirm-password-input"
                className="w-full bg-transparent border-b border-white/20 py-3 text-sm focus:border-gold focus:outline-none"
                placeholder="Repite la contrasena" />
            </div>
            <button type="submit" disabled={loading} data-testid="confirm-reset-btn"
              className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors disabled:opacity-50">
              {loading ? 'Cambiando...' : 'Cambiar Contrasena'}
            </button>
          </form>
        )}

        {step === 3 && (
          <div className="text-center space-y-6">
            <div className="w-16 h-16 mx-auto bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center">
              <Check size={32} className="text-emerald-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Contrasena Actualizada</h3>
              <p className="text-sm text-white/50">Tu contrasena ha sido cambiada exitosamente.</p>
            </div>
            <button onClick={() => navigate('/login')} data-testid="go-login-btn"
              className="w-full py-3 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors">
              Iniciar Sesion
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
